import React, { useEffect, useRef } from 'react';

import { FORM_TYPE_CREATE_ELT } from 'js/constants/formTypes';
import * as PATHS from 'js/constants/pathnames';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import FlowParamsProvider from 'new/components/flows/FlowParamsProvider';
import useFlowParams from 'new/components/flows/useFlowParams';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import flowTypes from 'new/components/flows/types/flowTypes';
import { EnterCredentialPage } from 'new/components/flows/types/elt/CreateELTFlow/EnterCredentialPage';
import { SelectConnectionPage } from 'new/components/flows/types/elt/CreateELTFlow/SelectConnectionPage';
import { SourceSetupPage } from 'new/components/flows/types/elt/CreateELTFlow/SourceSetupPage';
import { Form } from 'new/elements/Form';
import { Header } from 'new/elements/Form/Header';
import { flowDataMap } from 'new/elements/flows/dataMap';

import { DestinationSetupPage } from './DestinationSetupPage';

interface CreateELTFlowProps {
  id?: number;
  flowType?: keyof typeof flowTypes;
  onDismiss?: (data: any) => void;
  isModal?: boolean;
  isEditMode?: boolean;
  resourceType?: 'source' | 'sink';
}

const CreateELTFlow = (props: CreateELTFlowProps) => {
  const { isModal, onDismiss } = props;
  const { resourceType, id } = useFlowParams();
  const start = useRef(true);

  const {
    data: formData,
    updateFormData,
    resetFormData,
    end: endForm,
    start: startForm,
  } = useFlowsSetup(FORM_TYPE_CREATE_ELT, flowTypes.elt);

  const connectionType = formData?.connectionType;

  const data = flowDataMap.elt;

  if (start.current) {
    startForm();
    updateFormData({
      connectionType: resourceType.toUpperCase(),
      flowType: flowTypes.elt,
    });
    start.current = false;
  }

  function resetFields() {
    resetFormData([
      'credentialId',
      'selectedCredentialInfo',
      'selectedConnection',
      'selectedBucket',
      'selectedTable',
      'table',
      'source',
      'sink',
      'nexset',
      'isNewTable',
      'sourceConfig',
      'sinkConfig',
      'templateConfig',
      'discoveryResults',
      'selectedFeeds',
      'feedFields',
      'feedFieldsLoading',
      'expandedFeeds',
      'description',
      'vendorFeedSpecs',
      'availableFeeds',
      'templateVersionInfo',
    ]);
  }

  useEffect(() => {
    resetFields();
    return () => {
      resetFields();
      if (connectionType === 'SINK') {
        endForm();
      }
    };
  }, []);

  const onFormDismiss = () => {
    endForm();
    if (isModal) {
      onDismiss?.(formData);
      return true;
    }
    return false;
  };

  return (
    <Form
      formType={FORM_TYPE_CREATE_ELT}
      isModal={isModal}
      isEditMode={!!id}
      onDismiss={onFormDismiss}
      HeaderComponent={
        <Header
          title={
            <>
              {data.shortTitle} <span style={{ color: 'var(--color-greyscale-200)' }}>/</span>{' '}
              {id
                ? `Editing ${resourceType === 'source' ? 'Source' : 'Destination'}`
                : `New ${resourceType === 'source' ? 'Source' : 'Destination'}`}
            </>
          }
          description=""
        />
      }
      description=""
      startPage={!!id ? 2 : undefined}
    >
      <SelectConnectionPage />
      <EnterCredentialPage />
      {resourceType === 'sink' ? <DestinationSetupPage /> : <SourceSetupPage />}
    </Form>
  );
};

const CreateELTFlowContent = (props: CreateELTFlowProps) => {
  return (
    <FlowParamsProvider {...props}>
      <CreateELTFlow {...props} />
    </FlowParamsProvider>
  );
};

// Register screen configuration so breadcrumbs and titles are populated
(CreateELTFlowContent as any).screenConfig = {
  screenName: 'CreateELTFlow',
  domain: 'main',
  path: [
    `${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/elt/:resourceType/new`,
    `${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/elt/:resourceType/:id/edit`,
  ],
  header: {
    title: 'ELT',
    slugTitle: 'ELT',
  },
  hideSideBar: true,
};

export default CreateELTFlowContent;
